import React, { useState } from 'react';
import { useMutation, ApolloError } from '@apollo/client';
import { Input, useFlashMessage, Button } from '@indigo-ag/component-module';

import i18next from 'core/i18n';
import { createStylesHook, EnhancedPaper, utils } from 'shared';
import { addNewCampaign, allCampaigns } from 'features/admin/adminQueries';

const useStyles = createStylesHook(theme => ({
  input: {
    '& label': {
      color: '#5B6B72',
      padding: '0 0 0.25rem 0'
    },
    '& div[role=group]': {
      margin: '0 10px 0 0',
      fontSize: theme.typography.pxToRem(14),
      border: '1px solid #ACC0CA',
      height: '48px',
      borderRadius: '4px',
      '& input': {
        fontWeight: 'normal',
        fontSize: '16px',
        '&:placeholder': {
          color: '#899CA5'
        }
      }
    }
  },
  mr1dot5: {
    marginRight: '1.5rem'
  },
  dInlineBlock: {
    display: 'inline-block'
  }
}));

export const AddNewCampaign: React.FunctionComponent = () => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * State
   */
  const [campaignName, setCampaignName] = useState('');
  const [campaignSfdcId, setCampaignSfdcId] = useState('');
  const [isAddingCampaign, setIsAddingCampaign] = useState(false);

  /**
   * Mutations
   */
  const [addNewCampaignFn] = useMutation(addNewCampaign);

  const { addFlashMessage } = useFlashMessage();

  const addCampaign = () => {
    setIsAddingCampaign(true);

    addNewCampaignFn({
      variables: {
        newCampaignInfo: {
          name: campaignName,
          salesforceId: campaignSfdcId
        }
      },
      refetchQueries: [{ query: allCampaigns }]
    })
      .then(() => {
        setCampaignName('');
        setCampaignSfdcId('');
        addFlashMessage({
          content: i18next.t('campaignAdded'),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setIsAddingCampaign(false));
  };

  return (
    <EnhancedPaper
      style={{
        marginBottom: '20px',
        paddingBottom: '7px',
        boxShadow: '0px 2px 4px rgba(0, 87, 125, 0.14), 0px 2px 2px rgba(0, 87, 125, 0.1)'
      }}
    >
      <div
        style={{
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div className={`${classes.mr1dot5} ${classes.dInlineBlock}`}>
          <Input
            label={utils.text.toTitleCase(i18next.t('campaignName'))}
            className={classes.input}
            placeholder="Milho Verão 21/21 - Summer Corn Brazil"
            onChange={e => setCampaignName(e.target.value)}
            value={campaignName}
            sizeVariant="slim"
            style={{ padding: '2px', width: '300px' }}
          />
        </div>
        <div className={`${classes.mr1dot5} ${classes.dInlineBlock}`}>
          <Input
            label={utils.text.toTitleCase(i18next.t('campaignId'))}
            className={classes.input}
            placeholder="7011F000000QcuLQAS"
            onChange={e => setCampaignSfdcId(e.target.value)}
            value={campaignSfdcId}
            sizeVariant="slim"
            style={{ padding: '2px', width: '300px' }}
          />
        </div>
        <div className={classes.dInlineBlock}>
          <Button
            type="button"
            size="small"
            disabled={!(campaignName.length && campaignSfdcId.length)}
            onClick={addCampaign}
            busy={isAddingCampaign}
          >
            {i18next.t('add')}
          </Button>
        </div>
      </div>
    </EnhancedPaper>
  );
};
